import React, { useState } from 'react';
import styled from 'styled-components';
import { ButtonSkeleton, Poppins, Spacer, TableSkeleton } from 'src/common';
import GenericTable, { TableSheetScenarioLibrary, useScenarioLibraryColumns } from 'src/components/GenericTable';
import Button from 'src/components/form/Button';
import { AssessmentSelect } from 'src/components/select';
import { useMutation } from 'react-query';
import { addTemplatesToAssessment } from 'src/api/scenarioTemplates';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { ScenarioLibraryItem } from 'src/components/GenericTable/sharedColumns';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { ConfirmDeleteModal } from 'src/components/modals/ConfirmDeleteModal';

const Section = styled.div`
  .panel {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    grid-gap: 10px;
    &__add-btn {
      align-self: flex-end;
      justify-self: flex-end;
      margin-left: auto;
    }

    &__controls {
      display: flex;
      align-items: center;
      grid-gap: 10px;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
  }
  .success {
    color: ${({ theme }) => theme.colors.success};
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    .panel {
      flex: 1;
      &__controls {
        flex-direction: column;
        align-items: flex-end;
        flex: 1;
      }
    }
  }
`;

interface ScenarioLibraryTableSectionProps {
  data?: ScenarioLibraryItem[];
  isLoading: boolean;
  tableId: TableIds;
  title?: string;
  canDelete?: boolean;
  isDeleting?: boolean;
  onDeleteScenarios?: (ids: string[]) => Promise<void> | void;
}

export const ScenarioLibraryTableSection: React.FC<ScenarioLibraryTableSectionProps> = ({
  data = [],
  isLoading,
  tableId,
  title,
  canDelete,
  isDeleting,
  onDeleteScenarios,
}) => {
  const [mode, setMode] = useState<'add' | 'delete' | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [assessmentId, setAssessmentId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const { columns, checked, setChecked, GTColumnSelectAnchorExported } = useScenarioLibraryColumns({
    data,
    isCheckerEnabled: mode !== null,
    tableId,
    openNewTab: false,
  });

  const [add, { isLoading: isAdding }] = useMutation(addTemplatesToAssessment, {
    onMutate: () => setErr(null),
    onSuccess: () => {
      setMode(null);
      setChecked([]);
      setAssessmentId('');
      showSuccessToast(checked.length === 1 ? toastMessages.scenario.created : toastMessages.scenario.createdPlural);
    },

    onError: (err: any) => setErr(err.message || 'Something went wrong'),
  });

  return (
    <Section>
      <div className="panel">
        {mode === 'add' ? (
          <>
            <Poppins px={22} color="cflowerBlue" css="justify-self: flex-start;margin-right: auto;">
              Select assessment and scenarios
            </Poppins>
            <div className="panel__controls">
              <AssessmentSelect value={assessmentId} onChange={(el) => setAssessmentId(el.value)} />
              <Button
                onClick={() => {
                  setMode(null);
                  setChecked([]);
                  setAssessmentId('');
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Cancel',
                    tags: ['ASSESSMENT', 'SCENARIO'],
                  });
                }}
                secondary
                $constWidth={150}
                disabled={isAdding}
              >
                CANCEL
              </Button>
              <Button
                onClick={() => {
                  add({
                    assessments: [assessmentId],
                    templates: checked,
                  });
                  mpEvent(MPEvents.ButtonClick, {
                    button: 'Add',
                    tags: ['ASSESSMENT', 'SCENARIO'],
                  });
                }}
                primary
                disabled={!checked.length || isAdding || !assessmentId}
                loading={isAdding}
                data-cy="add-to-assessment-confirm"
                $constWidth={150}
              >
                + ADD
              </Button>
            </div>
          </>
        ) : mode === 'delete' ? (
          <>
            <Poppins px={22} color="cflowerBlue" css="justify-self: flex-start;margin-right: auto;">
              Select risks to delete
            </Poppins>
            <div className="panel__controls">
              <Button
                onClick={() => {
                  setMode(null);
                  setChecked([]);
                }}
                secondary
                $constWidth={150}
                disabled={isDeleting}
              >
                CANCEL
              </Button>
              <Button
                onClick={() => setIsDeleteConfirmOpen(true)}
                primary
                disabled={!checked.length || isDeleting}
                loading={isDeleting}
                data-cy="delete-scenario-pack-risks-confirm-open"
                $constWidth={150}
              >
                DELETE
              </Button>
            </div>
          </>
        ) : (
          <>
            {title && (
              <Poppins px={32} weight={600}>
                {title}
              </Poppins>
            )}
            {!isLoading ? (
              <div className="panel__controls panel__add-btn">
                {canDelete && (
                  <Button
                    onClick={() => {
                      setMode('delete');
                      mpEvent(MPEvents.ButtonClick, {
                        button: 'Delete',
                        tags: ['SCENARIO TEMPLATE'],
                      });
                    }}
                    secondary
                    data-cy="delete-scenario-pack-risks"
                  >
                    DELETE RISKS
                  </Button>
                )}
                <Button
                  onClick={() => {
                    setMode('add');
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Add to assessment',
                      tags: ['ASSESSMENT', 'SCENARIO'],
                    });
                  }}
                  primary
                  data-cy="add-to-assessment"
                >
                  + ADD TO ASSESSMENT
                </Button>
              </div>
            ) : (
              <ButtonSkeleton />
            )}
          </>
        )}
      </div>
      {err && (
        <>
          <Spacer $px={15} />
          <Poppins className="error" px={14}>
            {err}
          </Poppins>
        </>
      )}
      <Spacer $px={30} />
      {!isLoading && data ? (
        <GenericTable
          tableId={tableId}
          data={data}
          columns={columns}
          GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
          expandContent={(row) => <TableSheetScenarioLibrary data={row} />}
          searchable={['author', 'name', 'source', 'event', 'consequence', 'description']}
        />
      ) : (
        <TableSkeleton />
      )}
      {isDeleteConfirmOpen && (
        <ConfirmDeleteModal
          title={`Delete ${checked.length} ${checked.length === 1 ? 'risk' : 'risks'}?`}
          confirmText="DELETE"
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={async () => {
            try {
              await onDeleteScenarios?.(checked);
              setIsDeleteConfirmOpen(false);
              setMode(null);
              setChecked([]);
            } catch (error: any) {
              setIsDeleteConfirmOpen(false);
              setErr(error?.message || 'Could not delete selected risks.');
            }
          }}
        >
          <Poppins px={14} color="cflowerBlue">
            This creates a new scenario pack version without the selected risks.
          </Poppins>
        </ConfirmDeleteModal>
      )}
    </Section>
  );
};
